import { httpClient } from '@/utils/http-client'
import { AxiosResponse } from 'axios'
import {
    DepositBalanceAsOfDateData,
    HomeMetricsForDateRangeData,
    LiabilitiesData,
    MonthlySpendingComparsionData,
    RecordSnapshotRetrievalStrategy,
    TopMerchantsByTotalAmountDescendingData,
} from 'aven_types'

interface ResponseData<T> {
    success: boolean
    payload: T
    error: string
}

// Personal Financial Statement APIs
export interface GetPersonalFinancialStatementInfoAsOfDatePayload {
    snapshotDate: Date
    sourceUniqueId: string | null
    retrievalStrategy: RecordSnapshotRetrievalStrategy
    rateLimitingOptions: {
        turnOffRateLimiter: boolean
        onlyCountSuccessfulFetchesWhenRateLimiting: boolean
    }
    fetchNewRecordSnapshotsOptions: {
        doNotFetchNewRecordsFromSource: boolean
        fetchKeyStatusesFromSourceValues: boolean
    }
}

export const getNetWorthAsOfDate = async (payload: GetPersonalFinancialStatementInfoAsOfDatePayload): Promise<AxiosResponse<ResponseData<number>>> => {
    return await httpClient.post('/avenAdvisor/getNetWorthAsOfDate', payload)
}

export const getMostRecentDepositBalancesAsOfDate = async (payload: GetPersonalFinancialStatementInfoAsOfDatePayload): Promise<AxiosResponse<ResponseData<DepositBalanceAsOfDateData[]>>> => {
    return await httpClient.post('/avenAdvisor/getMostRecentDepositBalancesAsOfDate', payload)
}

export interface GetPersonalFinancialStatementInfoForDateRangePayload {
    dateRange: {
        startDate: Date
        endDate: Date
    }
    sourceUniqueId: string | null
    retrievalStrategy: RecordSnapshotRetrievalStrategy
    rateLimitingOptions: {
        turnOffRateLimiter: boolean
        onlyCountSuccessfulFetchesWhenRateLimiting: boolean
    }
    fetchNewRecordSnapshotsOptions: {
        doNotFetchNewRecordsFromSource: boolean
        fetchKeyStatusesFromSourceValues: boolean
        onlyFetchBetweenMostRecentSnapshotAndCurrentDate: boolean
        fetchSnapshotDateRangeOverride: {
            startDate: Date
            endDate: Date
        } | null
    }
}

export interface GetPersonalFinancialStatementTimeSeriesInfoForDateRangePayload extends GetPersonalFinancialStatementInfoForDateRangePayload {
    timeIntervalMs: number
}

export const getNetWorthForDateRange = async (payload: GetPersonalFinancialStatementTimeSeriesInfoForDateRangePayload): Promise<AxiosResponse<ResponseData<Array<{ time: Date; value: number }>>>> => {
    return await httpClient.post('/avenAdvisor/getNetWorthForDateRange', payload)
}

export const getHomeMetricsForDateRange = async (payload: GetPersonalFinancialStatementTimeSeriesInfoForDateRangePayload): Promise<AxiosResponse<ResponseData<HomeMetricsForDateRangeData[]>>> => {
    return await httpClient.post('/avenAdvisor/getHomeMetricsForDateRange', payload)
}

export const getTopMerchantsForDateRange = async (payload: GetPersonalFinancialStatementInfoForDateRangePayload): Promise<AxiosResponse<ResponseData<TopMerchantsByTotalAmountDescendingData>>> => {
    return await httpClient.post('/avenAdvisor/getTopMerchantsForDateRange', payload)
}

export const getMonthlySpendingComparisonData = async (): Promise<AxiosResponse<ResponseData<MonthlySpendingComparsionData>>> => {
    return await httpClient.get('/avenAdvisor/getMonthlySpendingComparsionData')
}

export const getLiabilitiesAsOfDate = async (payload: GetPersonalFinancialStatementInfoAsOfDatePayload): Promise<AxiosResponse<ResponseData<LiabilitiesData>>> => {
    return await httpClient.post('/avenAdvisor/getLiabilities', payload)
}
